import React from "react";
import { z } from "zod";

import { FormDateRangePicker, FormField, FormRadioButtons, PanelFilters } from "@performyard/py-components";
import type { DateRange } from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../components/Inputs/SelectEmployee";
import SelectWorkflow from "./SelectWorkflow";

export const TODO_STATUS_OPTIONS = [
	{ label: "Any", value: "any" },
	{ label: "Active", value: "active" },
	{ label: "Complete", value: "completed" },
	{ label: "Overdue", value: "overdue" },
];

const NamedReferenceSchema = z.object({ id: z.string(), name: z.string() });

type NamedReference = z.infer<typeof NamedReferenceSchema>;

export type TodosFilters = {
	assignee?: NamedReference | null;
	workflow?: NamedReference | null;
	actor?: NamedReference | null;
	status?: "active" | "completed" | "overdue";
	dueDateStart?: Date;
	dueDateEnd?: Date;
	completedDateStart?: Date;
	completedDateEnd?: Date;
};

const DateRangeSchema = z.object({ from: z.date().optional(), to: z.date().optional() }).optional();

const TodosFilterFormSchema = z.object({
	assignee: NamedReferenceSchema.nullable().default(null),
	workflow: NamedReferenceSchema.nullable().default(null),
	actor: NamedReferenceSchema.nullable().default(null),
	status: z.enum(["any", "active", "completed", "overdue"]).default("active"),
	dueDate: DateRangeSchema,
	completedDate: DateRangeSchema,
});

type TodosFilterForm = z.infer<typeof TodosFilterFormSchema>;

const toDateRange = (start?: Date, end?: Date): DateRange | undefined =>
	start || end ? { from: start, to: end } : undefined;

type TodosFilterPanelProps = {
	isOpen: boolean;
	onClose: () => void;
	filters: Partial<TodosFilters>;
	onApplyFilters: (filters: Partial<TodosFilters>) => void;
};

const TodosFilterPanel = ({ isOpen, onClose, filters, onApplyFilters }: TodosFilterPanelProps) => {
	const handleSubmit = ({ value }: { value: TodosFilterForm }) => {
		const applied: Partial<TodosFilters> = {};
		if (value.assignee) applied.assignee = value.assignee;
		if (value.workflow) applied.workflow = value.workflow;
		if (value.actor) applied.actor = value.actor;
		if (value.status !== "any") applied.status = value.status;
		if (value.dueDate?.from) applied.dueDateStart = value.dueDate.from;
		if (value.dueDate?.to) applied.dueDateEnd = value.dueDate.to;
		if (value.completedDate?.from) applied.completedDateStart = value.completedDate.from;
		if (value.completedDate?.to) applied.completedDateEnd = value.completedDate.to;

		onApplyFilters(applied);
		onClose();
	};

	const defaultValues: TodosFilterForm = {
		assignee: filters.assignee || null,
		workflow: filters.workflow || null,
		actor: filters.actor || null,
		status: filters.status || "any",
		dueDate: toDateRange(filters.dueDateStart, filters.dueDateEnd),
		completedDate: toDateRange(filters.completedDateStart, filters.completedDateEnd),
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={TodosFilterFormSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
			headerWeight="bold"
		>
			<div className="space-y-4">
				<FormField<{ id: string; name: string } | null>
					id="assignee"
					label="Assignee"
					input={(field) => (
						<SingleSelectEmployee
							value={field.value}
							onChange={(employeeId, employeeName) =>
								field.onChange(
									employeeId && employeeName ? { id: employeeId, name: employeeName } : null
								)
							}
							placeholder="Make a selection"
							data-testid="assigneeFilter"
						/>
					)}
				/>

				<FormField<{ id: string; name: string } | null>
					id="workflow"
					label="Workflow"
					input={(field) => (
						<SelectWorkflow
							value={field.value}
							onChange={field.onChange}
							placeholder="Make a selection"
							data-testid="workflowFilter"
						/>
					)}
				/>

				<FormField<{ id: string; name: string } | null>
					id="actor"
					label="Actor"
					input={(field) => (
						<SingleSelectEmployee
							value={field.value}
							onChange={(employeeId, employeeName) =>
								field.onChange(
									employeeId && employeeName ? { id: employeeId, name: employeeName } : null
								)
							}
							placeholder="Make a selection"
							data-testid="actorFilter"
						/>
					)}
				/>

				<FormRadioButtons id="status" label="Status" options={TODO_STATUS_OPTIONS} />

				<FormDateRangePicker id="dueDate" label="Due Date" hasClearButton />

				<FormDateRangePicker id="completedDate" label="Completed Date" hasClearButton />
			</div>
		</PanelFilters>
	);
};

export default TodosFilterPanel;
